import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { makeStyles } from '@material-ui/styles';
import { Collapse, List, ListItemIcon, ListItemText, Typography } from '@material-ui/core';
import ListItemButton from '@material-ui/core/ListItemButton';

// project imports
import NavItem from '../NavItem';

// assets
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { IconChevronDown, IconChevronUp } from '@tabler/icons';
import useAuth from 'hooks/useAuth';
import { useNavigate } from 'react-router';
import { SET_ACTIVE_MENU_ID } from 'store/actions';

// style constant
const useStyles = makeStyles((theme) => ({
    collapseIcon: {
        fontSize: '1rem',
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    collapseIconSub: {
        fontSize: '1rem',
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    menuIcon: {
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    listIcon: {
        minWidth: '18px',
        marginTop: 'auto',
        marginBottom: 'auto'
    },
    listCustomIconSub: {
        width: '6px',
        height: '6px'
    },
    listCustomIconSubActive: {
        width: '8px',
        height: '8px'
    },
    listItem: {
        marginBottom: '5px',
        alignItems: 'flex-start'
    },
    listItemNoBack: {
        marginBottom: '5px',
        backgroundColor: 'transparent !important',
        paddingTop: '8px',
        paddingBottom: '8px',
        alignItems: 'flex-start'
    },
    subMenuCaption: {
        ...theme.typography.subMenuCaption
    },
    collapseWrapper: {
        position: 'relative',
        '&:after': {
            content: "''",
            position: 'absolute',
            left: '32px',
            top: 0,
            height: '100%',
            width: '1px',
            opacity: theme.palette.mode === 'dark' ? 0.2 : 1,
            background: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary.light
        }
    }
}));

// ===========================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||=========================== //

const onlySuperAdminMenus = ['unapproved-users-list', 'rp-anukulVyakti-suchi'];
const NavCollapse = ({ menu, level, activeMenuId, setActiveMenuId }) => {
    const classes = useStyles();
    const { user } = useAuth();
    const navigate = useNavigate();
    const customization = useSelector((state) => state.customization);
    const dispatch = useDispatch();

    const isOpen = useMemo(() => activeMenuId === menu?.id, [activeMenuId, menu?.id]);

    // const [open, setOpen] = React.useState(false);
    // const [selected, setSelected] = React.useState(null);

    const handleClick = (e) => {
        // console.log('rendering handle click for list collapse', menu, level);
        // setOpen(!open);
        setActiveMenuId(isOpen ? null : menu?.id);
        dispatch({ type: SET_ACTIVE_MENU_ID, menuId: menu?.id });

        if (+user.profile.level.level_id <= 7 && (menu?.id === 'sankalp' || menu?.id === 'sadyashtiti')) {
            navigate(menu.url);
        }
        // setSelected(!selected ? menu?.id : null);
    };

    // menu list collapse & items
    let filteredMenus = menu.children;
    if (user && user.claims && !user.claims.admin) {
        filteredMenus = menu.children.filter((m) => onlySuperAdminMenus.indexOf(m.id) < 0);
    }
    const menus = filteredMenus.map((item) => {
        switch (item?.type) {
            case 'collapse':
                return (
                    <NavCollapse
                        key={item?.id}
                        menu={item}
                        level={level + 1}
                        activeMenuId={activeMenuId}
                        setActiveMenuId={setActiveMenuId}
                    />
                );
            case 'item':
                return <NavItem key={item?.id} item={item} level={level + 1} />;
            default:
                return (
                    <Typography key={item?.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const Icon = menu.icon;
    const menuIcon = menu.icon ? (
        <Icon stroke={1.5} size="1.3rem" className={classes.listCustomIcon} />
    ) : (
        <FiberManualRecordIcon
            className={isOpen ? classes.listCustomIconSubActive : classes.listCustomIconSub}
            fontSize={level > 0 ? 'inherit' : 'default'}
        />
    );

    const menuIconClass = !menu.icon ? classes.listIcon : classes.menuIcon;

    return (
        <>
            <ListItemButton
                className={level > 1 ? classes.listItemNoBack : classes.listItem}
                sx={{ borderRadius: `${customization.borderRadius}px` }}
                selected={isOpen}
                onClick={(e) => handleClick(e)}
                style={{ paddingLeft: `${level * 23}px` }}
            >
                <ListItemIcon className={menuIconClass}>{menuIcon}</ListItemIcon>
                <ListItemText
                    primary={
                        <Typography variant={isOpen ? 'h5' : 'body1'} color="inherit" className={classes.listItemTypography}>
                            {menu.title}
                        </Typography>
                    }
                    secondary={
                        menu.caption && (
                            <Typography variant="caption" className={classes.subMenuCaption} display="block" gutterBottom>
                                {menu.caption}
                            </Typography>
                        )
                    }
                />
                {isOpen ? (
                    <IconChevronUp stroke={1.5} size="1rem" className={level > 1 ? classes.collapseIconSub : classes.collapseIcon} />
                ) : (
                    <IconChevronDown stroke={1.5} size="1rem" className={level > 1 ? classes.collapseIconSub : classes.collapseIcon} />
                )}
            </ListItemButton>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding className={classes.collapseWrapper}>
                    {menus}
                </List>
            </Collapse>
        </>
    );
};

NavCollapse.propTypes = {
    menu: PropTypes.object,
    level: PropTypes.number,
    activeMenuId: PropTypes.string,
    setActiveMenuId: PropTypes.func
};

export default NavCollapse;
